'use client';

import { FormParticipantDataProps } from '@/components/common/forms/form-participant-data/FormParticipantData.types';
import TermsAndConditions from '@/components/modals/termsAndConditions/TermsAndConditions';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import useMixPanel from '@/hooks/useMixPanel';
import { useRootStore } from '@/providers/root-store-provider';
import formParticipantDataSchema from '@/schemas/formParticipantData';
import { StepService } from '@/stores/slices/step-service/step-services.types';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader } from 'lucide-react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { TForm } from './FormParticipantData.types';
import ParticipantForm from './components/ParticipantForm';
import UserForm from './components/UserForm';

const FormParticipantData: React.FC<FormParticipantDataProps> = ({
  user,
  organization,
  checkUserEmail,
  emailRef,
  isPending,
  onSubmit
}) => {
  const { t } = useTranslation(['newBooking', 'common']);
  const Mixpanel = useMixPanel();

  //store
  const removeStep = useRootStore((store) => store.removeStep);
  const stepServicesData: StepService | null = useRootStore(
    (store) => store.stepServicesData
  );
  const currentStep = useRootStore((store) => store.currentStep);
  const minAge = stepServicesData?.service?.minAge;
  const maxAge = stepServicesData?.service?.maxAge;

  const defaultValues = {
    participants: [
      ...Array(
        stepServicesData?.participants ? stepServicesData?.participants - 1 : 1
      )
    ].map(() => ({
      firstName: '',
      lastName: '',
      birthdate: undefined,
      height: 0,
      weight: 0
    })),
    user: {
      email: user?.email ? user.email : '',
      firstName: user?.firstName ? user.firstName : '',
      lastName: user?.lastName ? user.lastName : '',
      birthdate: user?.birthdate ? new Date(user.birthdate) : undefined,
      height: user?.height ? user.height : 0,
      weight: user?.weight ? user.weight : 0,
      phone: user?.phone ? user.phone : '',
      identification: {
        number: user?.identification?.number
          ? user?.identification?.number
          : '',
        type: user?.identification?.type ? user?.identification?.type : ''
      },
      userIsParticipant: true
    },
    acceptedTos: false,
    declarationLegalAge: false
  };

  const form = useForm<z.infer<TForm>>({
    resolver: zodResolver(formParticipantDataSchema({ minAge, maxAge })),
    // TODO: Encontrar error de TS o RHF por que algunos valores no le agrega el tpye indicado, en este caso los valores que TS: birthdate, height, weight
    // @ts-ignore
    defaultValues
  });

  const { fields } = useFieldArray({
    control: form.control,
    name: 'participants'
  });

  const userIsParticipant = form.getValues().user.userIsParticipant;

  const onBack = () => {
    removeStep(currentStep);
  };
  const userEmail = form.watch('user.email');

  const handleBlur = async () => {
    const isEmail = await form.trigger('user.email');
    if (userEmail !== '' && isEmail) {
      emailRef.current = userEmail;
      checkUserEmail();
    }
  };

  const formError = Boolean(Object.keys(form.formState.errors).length);
  return (
    <Form {...form}>
      <h2 className="font-bold mb-6">{t('stepParticipants.title')}</h2>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
        {/* User is participant Checkbox */}
        <FormField
          control={form.control}
          name={'user.userIsParticipant'}
          render={({ field }) => (
            <FormItem>
              <div className="space-y-3 p-2">
                <FormControl>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="user.userIsParticipant"
                      checked={!field.value}
                      onCheckedChange={() => {
                        // remove a participant when the user is participant
                        Mixpanel.track('press not for me checkbox', {
                          screen: 'New Booking'
                        });
                        if (stepServicesData?.participants) {
                          const currentValue = form.getValues().participants;
                          if (!field.value) {
                            form.setValue(
                              'participants',
                              currentValue.slice(1)
                            );
                          } else if (currentValue?.length === 0) {
                            form.setValue('participants', [
                              {
                                firstName: '',
                                lastName: '',
                                birthdate: new Date(),
                                height: 0,
                                weight: 0
                              }
                            ]);
                          } else {
                            form.setValue('participants', [
                              ...currentValue,
                              {
                                firstName: '',
                                lastName: '',
                                birthdate: new Date(),
                                height: 0,
                                weight: 0
                              }
                            ]);
                          }
                        }

                        form.clearErrors();
                        field.onChange(!field.value);
                      }}
                    />
                    <FormLabel htmlFor="userIsParticipant" className="ml-2">
                      {t('stepParticipants.userIsParticipantLabel')}
                    </FormLabel>
                  </div>
                </FormControl>
                <FormMessage />
              </div>
            </FormItem>
          )}
        />

        {/* mail input */}
        <FormField
          control={form.control}
          name="user.email"
          render={({ field }) => {
            const disabled = !!user?.email;
            return (
              <FormItem className="p-2">
                <FormLabel>{t('stepParticipants.form.mail')}</FormLabel>
                <FormControl>
                  <Input
                    onClick={() =>
                      Mixpanel.track('press email input', {
                        screen: 'New Booking'
                      })
                    }
                    readOnly={disabled}
                    placeholder="example@email.com"
                    type="email"
                    {...field}
                    onBlur={handleBlur}
                  />
                </FormControl>
                <FormDescription>
                  {t('stepParticipants.form.mail_description')}
                </FormDescription>
                <FormMessage />
              </FormItem>
            );
          }}
        />

        <div className="space-y-6 md:container">
          <UserForm
            form={form}
            userIsParticipant={userIsParticipant}
            user={user}
          />
          {fields.map((field, index) => (
            <ParticipantForm
              key={`participant-${index + 1}`}
              form={form}
              userIsParticipant={userIsParticipant}
              index={index}
            />
          ))}
        </div>

        {/* Terms and conditions  */}
        <FormField
          control={form.control}
          name="acceptedTos"
          render={({ field }) => (
            <FormItem>
              <div className="flex items-end">
                <FormControl>
                  <Checkbox
                    onClick={() =>
                      Mixpanel.track('press terms & cons', {
                        screen: 'New Booking'
                      })
                    }
                    id="isAccept"
                    checked={field.value}
                    onCheckedChange={() => field.onChange(!field.value)}
                  />
                </FormControl>
                <FormLabel htmlFor="isAccept" className="ml-2">
                  {t('stepParticipants.form.terms1') + ' '}
                  <TermsAndConditions
                    onClose={() => field.onChange(true)}
                    organization={organization}
                  />
                </FormLabel>
              </div>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Declaration of legal age */}
        <FormField
          control={form.control}
          name="declarationLegalAge"
          render={({ field }) => (
            <FormItem>
              <div className="flex items-end">
                <FormControl>
                  <Checkbox
                    onClick={() =>
                      Mixpanel.track('press declarationLegalAge', {
                        screen: 'New Booking'
                      })
                    }
                    id="declarationLegalAge"
                    checked={field.value}
                    onCheckedChange={() => field.onChange(!field.value)}
                  />
                </FormControl>
                <FormLabel htmlFor="declarationLegalAge" className="ml-2">
                  {t('declarationLegalAge')}
                </FormLabel>
              </div>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex flex-col gap-2">
          <Button
            type="submit"
            className="w-full"
            onClick={() =>
              Mixpanel.track('press continue button', {
                screen: 'New Booking'
              })
            }
            disabled={isPending}
          >
            {isPending ? (
              <Loader className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              t('stepParticipants.form.continue')
            )}
          </Button>
          <Button
            onClick={() => {
              onBack();
              Mixpanel.track('press go back button', {
                screen: 'New Booking'
              });
            }}
            type="button"
            variant="outline"
            className="w-full"
          >
            {t('stepParticipants.form.back')}
          </Button>
        </div>
        {formError && (
          <p className="text-sm font-medium text-destructive">
            {t('stepParticipants.form.errorMessage')}
          </p>
        )}
      </form>
    </Form>
  );
};

export default FormParticipantData;
